import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Input,
  Tooltip,
  Tree
} from 'syngenta-digital-cropwise-react-ui-kit';
import SortIcon from 'assets/sort.svg';
import filterOutlined from 'assets/filter_outlined.svg';
import { Spinner } from '@agconnections/grow-ui';
import {
  filterNodesBySearchFunction,
  obtainAllKeys
} from 'screens/Reports/helpers/helperFunctions';
import SearchIcon from 'components/Icons/SearchIcon';
import CheckIcon from '@material-ui/icons/Check';
import EmptyStateTree from './EmptyStateTree';
import ClickableDiv from '../ClickableDiv';

const ChecklistTree = ({
  treeData,
  hasActiveFilters,
  loading,
  checked,
  setChecked,
  setNumResults,
  onOpenFilters,
  enableFilters,
  emptyStateConfig,
  initialSelectAll,
  groupBy,
  onGroupByChange,
  groupByOptions
}) => {
  const [expanded, setExpanded] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [filteredTreeData, setFilteredTreeData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
  const { title, message, overlayIcon } = emptyStateConfig;
  const dropdownRef = useRef(null);

  const handleSelectAll = () => {
    if (checked.length === checkedKeys.length) {
      setChecked([]);
    } else {
      setChecked(checkedKeys);
    }
  };

  const onFilterChange = e => {
    const searchText = e.target.value;
    if (searchText?.length < 3) {
      setFilteredTreeData(treeData);
      return;
    }

    const filterNodes = filterNodesBySearchFunction(searchText);
    setFilteredTreeData(treeData.reduce(filterNodes, []));
  };

  const isEmptyStateTree = useMemo(
    () => (title.length ? treeData : treeData.length),
    [title, treeData]
  );

  useEffect(() => {
    if (isEmptyStateTree && !loading) {
      setFilteredTreeData(treeData);
      setInitialLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData]);

  useEffect(() => {
    setNumResults(filteredTreeData.length);

    const newCheckedKeys = obtainAllKeys(filteredTreeData);
    setCheckedKeys(newCheckedKeys);

    if (!initialSelectAll) return;

    if (initialLoad && filteredTreeData.length) {
      setChecked(newCheckedKeys);
      setInitialLoad(false);
      return;
    }
    setChecked(newCheckedKeys.filter(id => checked.includes(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTreeData]);

  // click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowGroupByDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleGroupByDropdown = () => {
    setShowGroupByDropdown(!showGroupByDropdown);
  };

  const handleGroupByChange = value => {
    onGroupByChange(value);
    setShowGroupByDropdown(false);
  };

  const isAllSelected = checked.length === checkedKeys.length;
  const isIndeterminate =
    checked.length > 0 && checked.length < checkedKeys.length;
  return (
    <div className="filter-container">
      {!loading ? (
        <>
          {isEmptyStateTree ? (
            <>
              <div className="mt-4 mb-2 flex flex-row items-center">
                <div className="flex-grow">
                  <Input
                    className="w-full"
                    prefix={<SearchIcon />}
                    placeholder="Search properties"
                    type="search"
                    onChange={onFilterChange}
                  />
                </div>
                {onGroupByChange &&
                  groupByOptions &&
                  groupByOptions.length > 1 && (
                    <div className="relative ml-2" ref={dropdownRef}>
                      <Tooltip title="Group by">
                        <button
                          data-testid="group-by-button"
                          type="button"
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-neutral-100 bg-white border border-neutral-30"
                          onClick={toggleGroupByDropdown}
                        >
                          <img src={SortIcon} alt="" />
                        </button>
                      </Tooltip>
                      {showGroupByDropdown && (
                        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-10 min-w-[160px]">
                          {groupByOptions.map(option => (
                            <ClickableDiv
                              key={option.value}
                              className={`gr-whitespace-nowrap pl-4 pr-6 py-2${
                                groupBy === option.value
                                  ? ' bg-selectedBlue font-semibold'
                                  : ''
                              }`}
                              onClick={() => handleGroupByChange(option.value)}
                              style={
                                groupBy !== option.value
                                  ? {
                                      cursor: 'pointer',
                                      transition: 'background 0.2s'
                                    }
                                  : undefined
                              }
                              onMouseEnter={e => {
                                if (groupBy !== option.value) {
                                  e.currentTarget.style.background = '#F3F4F6';
                                }
                              }}
                              onMouseLeave={e => {
                                if (groupBy !== option.value) {
                                  e.currentTarget.style.background = '';
                                }
                              }}
                            >
                              <CheckIcon
                                color={
                                  groupBy === option.value
                                    ? 'primary'
                                    : 'inherit'
                                }
                                className={`mr-2 ${
                                  groupBy === option.value ? '' : 'opacity-0'
                                }`}
                              />
                              {option.label}
                            </ClickableDiv>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                {enableFilters && (
                  <div className="relative ml-2">
                    <button
                      data-testid="panel-filter-button"
                      type="button"
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-neutral-100 bg-white border border-neutral-30"
                      onClick={onOpenFilters}
                    >
                      <img src={filterOutlined} alt="" />
                    </button>
                    {hasActiveFilters() && (
                      <div
                        className="w-10px h-10px rounded-full bg-blue-90 absolute top-0"
                        style={{ right: '-4px' }}
                      />
                    )}
                  </div>
                )}
              </div>
              {filteredTreeData.length ? (
                <>
                  <div className="mt-2 mb-1 flex">
                    <Checkbox
                      data-testid="checklist-tree-select-all"
                      size="small"
                      onChange={handleSelectAll}
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                    >
                      Select All
                    </Checkbox>
                  </div>
                  <Tree
                    checkable
                    treeData={filteredTreeData}
                    checkedKeys={checked}
                    onCheck={setChecked}
                    expandedKeys={expanded}
                    onExpand={setExpanded}
                    selectable={false}
                    showIcon={false}
                  />
                </>
              ) : (
                <div>
                  {title.length ? (
                    <EmptyStateTree
                      title={title}
                      message={message}
                      overlayIcon={overlayIcon}
                    />
                  ) : null}
                </div>
              )}
            </>
          ) : null}
        </>
      ) : (
        <div className="mt-40px">
          <Spinner size={72} />
        </div>
      )}
    </div>
  );
};

ChecklistTree.defaultProps = {
  treeData: [],
  loading: false,
  setNumResults: () => {},
  emptyStateConfig: { title: '', message: '', overlayIcon: '' },
  onOpenFilters: () => {},
  hasActiveFilters: () => {},
  enableFilters: false,
  initialSelectAll: true,
  groupBy: '',
  onGroupByChange: null,
  groupByOptions: []
};
ChecklistTree.propTypes = {
  treeData: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  checked: PropTypes.arrayOf(PropTypes.string).isRequired,
  setChecked: PropTypes.func.isRequired,
  setNumResults: PropTypes.func,
  emptyStateConfig: PropTypes.shape(),
  onOpenFilters: PropTypes.func,
  hasActiveFilters: PropTypes.func,
  enableFilters: PropTypes.bool,
  initialSelectAll: PropTypes.bool,
  groupBy: PropTypes.string,
  onGroupByChange: PropTypes.func,
  groupByOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  )
};

export default ChecklistTree;
